<template>
    <div class="view-menu-content" :style="menuStyle" :class="{'show-view-menu-content-class': viewMenuData.isOpenMenu}" v-show="viewMenuData.isOpenMenu">
        <div class="unfold-icon" @click="unfoldMenu"></div>
        <div class="menu-inner-content">
            <span class="menu-title">视图</span>
            <template v-for="item of menuList" :key="item.id">
                <div class="menu-item" :class="{ 'menu-active-item': viewMenuData?.selectedMenu?.id === item.id }" @click.self="selectMenu(item)">
                    <span class="menu-item-title" @click.self="selectMenu(item)" :title="item.name">{{ item.name }}</span>
                    <span class="handle-menu-icon" v-if="!isSystemView(item)">
                        <a-dropdown placement="bottomRight" :getPopupContainer="triggerNode => triggerNode.parentNode">
                            <template #overlay>
                                <a-menu class="menu-container">
                                    <a-menu-item key="detail" @click="renameView(item)" class="menu-handle-item">
                                        <span class="custom-icon-edit"></span>
                                        重命名
                                    </a-menu-item>
                                    <a-menu-item key="delete" @click="delView(item)" class="menu-handle-item">
                                        <span class="custom-icon-delete"></span>
                                        删除
                                    </a-menu-item>
                                </a-menu>
                            </template>
                            <span>
                                <Icon size="18" class="custom-icon-bgc" icon="ant-design:more-outlined" />
                            </span>
                        </a-dropdown>
                    </span>
                </div>
            </template>
        </div>
      <template v-if="reNameVisible">
        <FilterSaveModal
          :visible="reNameVisible"
          type="rename"
          :viewData="currentRenameView"
          @confirm="confirmHandel"
          @cancel="cancelHandel" />
      </template>
    </div>
</template>

<script lang="ts" setup name="ViewMenuContent">
import { computed, nextTick, onActivated, onMounted, ref, onDeactivated, unref } from 'vue';
import { useAppInject } from '/@/hooks/web/useAppInject';
import { useUserStore } from '/@/store/modules/user';
import { clearViewMenuEventBus, onDoneInitFusionFilterValue, onMenuViewUpdateChange, onOpenMenuChange, ViewMenuData } from '../config'
import { deleteMenuView, getMenuViews, saveMenuSelectedView } from "../api";
import { debounce } from 'lodash-es';
import { message } from "ant-design-vue";
import { useMessage } from "/@/hooks/web/useMessage";
import FilterSaveModal from '/@/components/FusionFilter/src/components/filterSaveModal.vue';


const { createConfirm } = useMessage()
const props = defineProps({
    type: {
        type: String,
    },
    viewMenuData: {
        type: Object as () => ViewMenuData
    },
})

const emit = defineEmits(['select', 'actived'])
const { getIsMobile } = useAppInject();
const { getTenant, getUserInfo } = useUserStore()
const menuList = ref([])
const saveSelectedFn = debounce(saveMenuSelectedView, 600)
const menuStyle = computed(() => ({
    height: `calc(100vh - ${unref(getIsMobile) ? 64 : 96}px)`,
}))

// 因为融合搜索有一些回显延迟操作，所以写了promise，用于以后类似的扩展
const initPromises = {
    fusionFilter: {
        resolve: null,
        initFn() {
            return new Promise(resolve => {
                this.resolve = resolve;
            })
        }
    }
}

const currentRenameView = ref(null);
const reNameVisible = ref(false);
function renameView(item) {
  currentRenameView.value = item;
  reNameVisible.value = true;
}

const confirmHandel = () => {
  reNameVisible.value = false;
}

const cancelHandel = () => {
  reNameVisible.value = false;
}

function delView(item) {
  createConfirm({
    iconType: 'warning',
    title: '删除视图',
    content: `您确定要删除 [${ item.name }] 视图吗？`,
    onOk: async() => {
      await deleteMenuView(item.id);
      message.success('删除成功');
      deleteMenuViewHandel(item.id);
    },
    onCancel: () => {},
  })
}

const deleteMenuViewHandel = (id) => {
    menuList.value = menuList.value.filter(item => item.id !== id);
    if (menuList.value.length > 0 && id === props.viewMenuData.selectedMenu.id ) {
        selectMenu(menuList.value[0])
    }
    updateViewCustomCount()
}

// 替换数据，如：${tenantId}替换为用户的tenantId值
function replaceFilterConfig() {
    const valueConfig = {
        tenantId: getTenant ?? '',
        userId: getUserInfo?.id ?? '',
    }

    if (!menuList.value?.length) {
        return;
    }

    menuList.value.forEach(item => {
        // 只有系统视图模板才需要替换
        if (!isSystemView(item)) {
            return;
        }
        const filterConfig = item.viewConfig?.filterConfig
        if (!Array.isArray(filterConfig) || !filterConfig.length) {
            return;
        }
        const filterConfigStr = JSON.stringify(filterConfig);
        const newStr = filterConfigStr.replace(/\$\{([\s\S]+?)\}/g, ($1, $2) => {
            return valueConfig[$2] || $1;
        })
        item.viewConfig.filterConfig = JSON.parse(newStr);
    })
}

function unfoldMenu() {
    props.viewMenuData.isOpenMenu = false;
    updateOpenMenu(props.viewMenuData.isOpenMenu);
}

async function selectMenu(item) {
    if (props.viewMenuData.selectedMenu === item) {
        return;
    }
    props.viewMenuData.selectedMenu = item;
    
    const params = {
        menuId: props.type,
        viewId: item.id,
        expandStatus: props.viewMenuData.isOpenMenu ? '1': '0',
    }
    
    saveSelectedFn(params);
    await setAllInitValue(item);
    emit('select');
}

function isSystemView(viewConfig) {
    return viewConfig?.type === 'sys';
}

async function initMenuList() {
    try {
        const res = await getMenuViews(props.type)
        menuList.value = res || [];
    } catch (error) {
        menuList.value = [];
    }

    if (menuList.value.length) {
      props.viewMenuData.selectedMenu = menuList.value.find(item => item?.hasChecked === '1') || menuList.value[0];
      props.viewMenuData.isOpenMenu = props.viewMenuData.selectedMenu?.expandStatus !== '0';
    }
    updateViewCustomCount()
    replaceFilterConfig()

    await setAllInitValue(props.viewMenuData.selectedMenu);
    emit('select');
}

const updateMenuList = (data) => {
    const { type, data: updateData } = data;
    if (type === 'update') {
        const index = menuList.value.findIndex(item => item.id === updateData.id);
        if (index !== -1) {
            menuList.value[index] = {...menuList.value[index], ...updateData};
            if (props.viewMenuData.selectedMenu?.id === updateData.id) {
                props.viewMenuData.selectedMenu = {...props.viewMenuData.selectedMenu,...updateData};
            }
        }
    } else if (type === 'add') {
        menuList.value.push(updateData);
        updateViewCustomCount()
    }
}

const updateViewCustomCount = () => {
    props.viewMenuData.viewCustomCount = menuList.value?.filter(item => !isSystemView(item))?.length ?? 0;
}
async function setAllInitValue(selectedMenu) {
    props.viewMenuData.searchConfig = selectedMenu?.searchConfig || '';
    await initPromises.fusionFilter.initFn();
}

function onDoneSetInitValue() {
    onDoneInitFusionFilterValue(() => {
        initPromises.fusionFilter.resolve()
    });
}

async function updateOpenMenu(value: boolean) {
    const params = {
        menuId: props.type,
        viewId: props.viewMenuData.selectedMenu?.id,
        expandStatus: value ? '1': '0',
    }

    saveSelectedFn(params);
}

let mounted = false;
onMounted(() => {
    initMenuList();
    nextTick(() => {
      mounted = true;
    });
});

// 在激活时，不请求视图数据，防止现有操作数据被覆盖，直接请求表格数据
onActivated(() => {
    onDoneSetInitValue();
    onOpenMenuChange(updateOpenMenu);
    if (mounted) {
        emit('actived');
    }
    onMenuViewUpdateChange(updateMenuList)
});

onDeactivated(() => {
    clearViewMenuEventBus();
})

</script>

<style lang="less" scoped>
@active-background-color: #E2EDFF;
@active-text-color: #005CFF;
.view-menu-content {
    width: 0;
    background-color: #fff;
    position: relative;
    border-right: 1px solid #E7EFFB;
    &:hover {
        .unfold-icon {
            display: inline-block;
        }
    }
    &.show-view-menu-content-class {
        width: 166px;
    }
}

.menu-inner-content {
    overflow-y: auto;
    overflow-x: hidden;
    padding: 34px 20px;
    max-height: 100%;
}
.unfold-icon {
    position: absolute;
    top: 34px;
    right: -14px;
    background-image: url('../assets/fold-icon.svg');
    box-shadow: 0px 0px 6px 0px rgba(17,36,74,0.12);
    border-radius: 50%;
    background-size: cover;
    width: 28px;
    height: 28px;
    cursor: pointer;
    display: none;
    &:hover {
        background-image: url('../assets/fold-hover-icon.svg');
    }
}
.menu-title {
    font-size: 16px;
    color: #262626;
    font-weight: 600;
    padding-left: 12px;
}
.menu-item {
    width: 126px;
    height: 40px;
    border-radius: 6px;
    margin-top: 12px;
    &:hover {
        background-color: @active-background-color;
        color: @active-text-color;
        cursor: pointer;
        .handle-menu-icon {
            display: flex;
            visibility: visible;
        }
    }
    display: flex;
    align-items: center;
    padding: 0 12px;
    justify-content: space-between;
    .handle-menu-icon {
      visibility: hidden;
      margin-left: 2px;
    }
}
.menu-item-title {
    max-width: 151px;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
}
.menu-active-item {
    background-color: @active-background-color;
    color: @active-text-color;
}
.menu-container {
    width: 88px;
    border-radius: 2px;
    padding: 12px 0;
    box-shadow: 0px 2px 4px 0px rgba(0,0,0,0.41);
    display: flex;
    flex-direction: column;
    justify-content: space-evenly;
}
:deep(.menu-container .menu-handle-item) {
  text-align: left ;
}
.custom-icon-bgc {
  background-color: #CFE1FF;
  vertical-align: -3px;
}
</style>
<style lang="less">
@active-background-color: #E2EDFF;
@active-text-color: #2C68FF;
.menu-handle-item {
    width: 100%;
    &:hover:not(.ant-dropdown-menu-item-disabled) {
        background-color: @active-background-color;
        color: @active-text-color;
    }
}
</style>
